import React, { useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery, useMutation } from 'react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  TextField,
  MenuItem,
  Stack,
  Alert,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Tooltip,
  Slider,
  Autocomplete,
  Chip,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  TrendingUp as ProfitIcon,
  ArrowBack as BackIcon,
  Calculate as CalcIcon,
} from '@mui/icons-material';
import { quotesApi, inventoryApi, projectsApi, quoteRequestsApi } from '../services/api.ts';
import {
  QuoteCreate,
  Project,
  EPCLookupResult,
  AGE_BAND_OPTIONS,
  QuoteRequest,
  QuoteRequestStatus,
  InventoryItem,
  InventoryCategory,
  INVENTORY_CATEGORY_LABELS,
} from '../types/index.ts';

// ── helpers ────────────────────────────────────────────────────────────────────

const fmtCurrency = (n: number) =>
  `£${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const extractError = (e: any): string => {
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => {
      const field = d.loc?.slice(1).join(' → ') || '';
      return field ? `${field}: ${d.msg}` : d.msg;
    }).join('; ');
  }
  return String(detail || 'An error occurred');
};

interface LineItemRow extends QuoteLineItemCreate {
  _key: string;
}

// ── Profitability panel ────────────────────────────────────────────────────────

interface ProfitPanelProps {
  lineItems: LineItemRow[];
  busGrant: number;
  targetMargin: number;
  onTargetMarginChange: (v: number) => void;
  onApplyMarkup: (markup: number) => void;
}

const ProfitPanel: React.FC<ProfitPanelProps> = ({
  lineItems,
  busGrant,
  targetMargin,
  onTargetMarginChange,
  onApplyMarkup,
}) => {
  const [markupInput, setMarkupInput] = useState('');

  const totalRevenue = lineItems.reduce((s, li) => s + li.quantity * li.unit_price, 0);
  const totalCost = lineItems.reduce(
    (s, li) => s + li.quantity * (li.cost_price ?? li.unit_price),
    0
  );
  const grossProfit = totalRevenue - totalCost;
  const grossMargin = totalRevenue > 0 ? (grossProfit / totalRevenue) * 100 : 0;
  const markup = totalCost > 0 ? (grossProfit / totalCost) * 100 : 0;
  const meetsTarget = grossMargin >= targetMargin;

  const vatTotal = lineItems.reduce(
    (s, li) => s + li.quantity * li.unit_price * (li.vat_rate / 100),
    0
  );
  const totalIncVat = totalRevenue + vatTotal;
  const totalAfterGrant = totalIncVat - busGrant;

  return (
    <Card
      sx={{
        border: '2px solid',
        borderColor: meetsTarget ? 'success.main' : 'warning.main',
      }}
    >
      <CardContent>
        <Stack direction="row" alignItems="center" spacing={1} mb={2}>
          <ProfitIcon color={meetsTarget ? 'success' : 'warning'} />
          <Typography variant="h6" fontWeight={700}>
            Profitability
          </Typography>
          <Chip
            label={meetsTarget ? 'Target met' : 'Below target'}
            color={meetsTarget ? 'success' : 'warning'}
            size="small"
          />
        </Stack>

        <Stack spacing={1.5}>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">Total revenue (ex. VAT)</Typography>
            <Typography fontWeight={600}>{fmtCurrency(totalRevenue)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">Total cost</Typography>
            <Typography fontWeight={600} color="error.main">
              −{fmtCurrency(totalCost)}
            </Typography>
          </Stack>
          <Divider />
          <Stack direction="row" justifyContent="space-between">
            <Typography fontWeight={700}>Gross profit</Typography>
            <Typography fontWeight={700} color={grossProfit >= 0 ? 'success.main' : 'error.main'}>
              {fmtCurrency(grossProfit)}
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">Gross margin</Typography>
            <Typography
              fontWeight={600}
              color={meetsTarget ? 'success.main' : 'warning.main'}
            >
              {grossMargin.toFixed(1)}%
            </Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">Markup on cost</Typography>
            <Typography fontWeight={600}>{markup.toFixed(1)}%</Typography>
          </Stack>

          <Divider />

          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">VAT</Typography>
            <Typography>{fmtCurrency(vatTotal)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">Total inc. VAT</Typography>
            <Typography fontWeight={600}>{fmtCurrency(totalIncVat)}</Typography>
          </Stack>
          <Stack direction="row" justifyContent="space-between">
            <Typography color="text.secondary">BUS grant</Typography>
            <Typography color="success.main">−{fmtCurrency(busGrant)}</Typography>
          </Stack>
          <Box sx={{ bgcolor: 'success.50', p: 1.5, borderRadius: 1 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography fontWeight={700} color="success.dark">Customer pays</Typography>
              <Typography variant="h6" fontWeight={700} color="success.dark">
                {fmtCurrency(Math.max(0, totalAfterGrant))}
              </Typography>
            </Stack>
          </Box>
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Typography variant="subtitle2" gutterBottom>
          Target gross margin: <strong>{targetMargin}%</strong>
        </Typography>
        <Slider
          value={targetMargin}
          min={0}
          max={60}
          step={1}
          onChange={(_, v) => onTargetMarginChange(v as number)}
          color={meetsTarget ? 'success' : 'warning'}
          valueLabelDisplay="auto"
          valueLabelFormat={(v) => `${v}%`}
          sx={{ mb: 1 }}
        />

        <Typography variant="subtitle2" gutterBottom sx={{ mt: 1 }}>
          Apply markup to hit margin
        </Typography>
        <Stack direction="row" spacing={1}>
          <TextField
            size="small"
            label="Markup %"
            type="number"
            value={markupInput}
            onChange={(e) => setMarkupInput(e.target.value)}
            inputProps={{ min: 0, max: 500, step: 1 }}
            sx={{ width: 120 }}
          />
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const m = parseFloat(markupInput);
              if (!isNaN(m) && m >= 0) onApplyMarkup(m);
            }}
          >
            Apply
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          Adjusts unit prices so markup on cost equals the entered %.
          Items without a cost price are skipped.
        </Typography>
      </CardContent>
    </Card>
  );
};

// ── Main calculator ────────────────────────────────────────────────────────────

const QuoteCalculatorPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const state = (location.state as any) ?? {};
  const quoteRequest: QuoteRequest | undefined = state.quoteRequest;
  // Pre-selected project when launched from ProjectDetailPage
  const preselectedProject: { id: number; title: string; project_number: string } | undefined =
    state.project;

  // Project search autocomplete
  const [projectSearch, setProjectSearch] = useState('');
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  const { data: projectSearchData } = useQuery(
    ['projects-search', projectSearch],
    () => projectsApi.getProjects({ search: projectSearch || undefined, limit: 20 }),
    { keepPreviousData: true, enabled: projectSearch.length >= 1 || !selectedProject }
  );

  // Initialise selectedProject from navigation state (launched from project page)
  React.useEffect(() => {
    if (preselectedProject && !selectedProject) {
      // Cast just enough to satisfy the Autocomplete — we only need id/title/project_number
      setSelectedProject(preselectedProject as unknown as Project);
    }
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // Property + EPC state
  const [postcode, setPostcode] = useState(quoteRequest?.postcode ?? '');
  const [epcResult, setEpcResult] = useState<EPCLookupResult | null>(null);
  const [epcLoading, setEpcLoading] = useState(false);
  const [epcError, setEpcError] = useState('');
  const [floorArea, setFloorArea] = useState('');
  const [ageBand, setAgeBand] = useState('');
  const [wallInsulation, setWallInsulation] = useState('');
  const [loftInsulation, setLoftInsulation] = useState('');
  const [glazingType, setGlazingType] = useState('');

  // Financial
  const [laborCost, setLaborCost] = useState('1500');
  const [busGrant, setBusGrant] = useState('7500');
  const [validDays, setValidDays] = useState('30');
  const [notes, setNotes] = useState('');

  // Profitability
  const [targetMargin, setTargetMargin] = useState(30);

  // Line items
  const [lineItems, setLineItems] = useState<LineItemRow[]>([]);

  // Inventory picker
  const [inventorySearch, setInventorySearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<InventoryCategory | ''>('');

  // Status
  const [error, setError] = useState('');
  const [heatLossResult, setHeatLossResult] = useState<any>(null);

  const { data: inventoryData } = useQuery(
    ['inventory', inventorySearch, categoryFilter],
    () =>
      inventoryApi.getInventory({
        search: inventorySearch || undefined,
        category: (categoryFilter as InventoryCategory) || undefined,
        in_stock_only: false,
        active_only: true,
        limit: 50,
      }),
    { keepPreviousData: true }
  );

  const createMutation = useMutation(quotesApi.createQuote, {
    onSuccess: (quote) => {
      // Link request if present
      if (quoteRequest) {
        quoteRequestsApi
          .updateRequest(quoteRequest.id, {
            status: QuoteRequestStatus.QUOTED,
            quote_id: quote.id,
          })
          .catch(() => {});
      }
      navigate(`/quotes/${quote.id}`);
    },
    onError: (e: any) => setError(extractError(e)),
  });

  // EPC lookup
  const handleEpcLookup = async () => {
    if (!postcode.trim()) return;
    setEpcLoading(true);
    setEpcError('');
    try {
      const result = await quotesApi.epcLookup(postcode.trim());
      setEpcResult(result);
      if (result.found) {
        if (result.floor_area_m2) setFloorArea(String(result.floor_area_m2));
        if (result.construction_age_band) setAgeBand(result.construction_age_band);

        // Auto-detect glazing type from windows_description
        if (result.windows_description) {
          const wd = result.windows_description.toLowerCase();
          if (wd.includes('triple')) setGlazingType('triple');
          else if (wd.includes('double')) setGlazingType('double');
          else if (wd.includes('single')) setGlazingType('single');
        }

        // Auto-detect wall insulation from walls_description
        if (result.walls_description) {
          const wds = result.walls_description.toLowerCase();
          if (wds.includes('cavity') && (wds.includes('insulated') || wds.includes('fill'))) {
            setWallInsulation('cavity_insulated');
          } else if (wds.includes('cavity')) {
            setWallInsulation('cavity_uninsulated');
          } else if (wds.includes('solid')) {
            setWallInsulation('solid');
          }
        }

        // Auto-detect loft insulation from roof_description
        if (result.roof_description) {
          const rd = result.roof_description.toLowerCase();
          if (rd.includes('200mm') || rd.includes('250mm') || rd.includes('270mm') || rd.includes('300mm') || rd.includes('100mm')) {
            setLoftInsulation('100mm+');
          } else if (rd.includes('50mm') || rd.includes('75mm')) {
            setLoftInsulation('<100mm');
          } else if (rd.includes('no insulation') || rd.includes('uninsulated')) {
            setLoftInsulation('none');
          }
        }
      }
    } catch (e: any) {
      setEpcError(extractError(e));
    } finally {
      setEpcLoading(false);
    }
  };

  // Add inventory item as line item
  const addInventoryItem = (item: InventoryItem) => {
    const key = `inv-${item.id}-${Date.now()}`;
    setLineItems((prev) => [
      ...prev,
      {
        _key: key,
        inventory_item_id: item.id,
        description: `${item.manufacturer} ${item.name}${item.capacity_kw ? ` ${item.capacity_kw} kW` : ''}`,
        quantity: 1,
        unit_price: item.unit_price,
        cost_price: item.cost_price ?? undefined,
        vat_rate: item.vat_rate,
        is_labor: false,
        sort_order: 0,
      },
    ]);
  };

  const addLabourLine = () => {
    const key = `labour-${Date.now()}`;
    const cost = parseFloat(laborCost) || 0;
    setLineItems((prev) => [
      ...prev,
      {
        _key: key,
        inventory_item_id: undefined,
        description: 'Installation, commissioning and first-year service',
        quantity: 1,
        unit_price: cost,
        cost_price: cost,
        vat_rate: 5,
        is_labor: true,
        sort_order: 0,
      },
    ]);
  };

  const addBlankLine = () => {
    setLineItems((prev) => [
      ...prev,
      {
        _key: `blank-${Date.now()}`,
        inventory_item_id: undefined,
        description: '',
        quantity: 1,
        unit_price: 0,
        cost_price: undefined,
        vat_rate: 5,
        is_labor: false,
        sort_order: 0,
      },
    ]);
  };

  const updateLineItem = (key: string, field: keyof LineItemRow, value: any) => {
    setLineItems((prev) =>
      prev.map((li) => (li._key === key ? { ...li, [field]: value } : li))
    );
  };

  const removeLineItem = (key: string) =>
    setLineItems((prev) => prev.filter((li) => li._key !== key));

  // Apply markup: new_price = cost * (1 + markup/100)
  const applyMarkup = useCallback(
    (markup: number) => {
      setLineItems((prev) =>
        prev.map((li) => {
          if (li.cost_price === undefined || li.cost_price === null) return li;
          const newPrice = li.cost_price * (1 + markup / 100);
          return { ...li, unit_price: Math.round(newPrice * 100) / 100 };
        })
      );
    },
    []
  );

  const handleGenerate = () => {
    setError('');
    if (!selectedProject) {
      setError('Select a project before generating the quote.');
      return;
    }
    const area = parseFloat(floorArea);
    if (!area) {
      setError('Floor area is required.');
      return;
    }

    const payload: QuoteCreate = {
      project_id: selectedProject.id,
      use_epc: false,
      floor_area_m2: area,
      age_string: ageBand || undefined,
      wall_insulation: wallInsulation || undefined,
      loft_insulation: loftInsulation || undefined,
      glazing_type: glazingType || undefined,
      labor_cost: parseFloat(laborCost) || 0,
      bus_grant: parseFloat(busGrant) || 0,
      valid_days: parseInt(validDays) || 30,
      notes: notes || undefined,
      line_items: lineItems.length > 0
        ? lineItems.map(({ _key, ...li }, i) => ({ ...li, sort_order: i }))
        : undefined,
    };

    createMutation.mutate(payload);
  };

  const busGrantNum = parseFloat(busGrant) || 0;
  const inventoryItems = inventoryData?.items ?? [];

  return (
    <Box p={3}>
      <Stack direction="row" alignItems="center" spacing={2} mb={3}>
        <Button startIcon={<BackIcon />} onClick={() => navigate(-1)} variant="outlined" size="small">
          Back
        </Button>
        <Box>
          <Typography variant="h4" fontWeight={700}>
            Quote Calculator
          </Typography>
          {quoteRequest && (
            <Typography variant="body2" color="text.secondary">
              From request: <strong>{quoteRequest.full_name}</strong> · {quoteRequest.postcode}
            </Typography>
          )}
        </Box>
      </Stack>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Left — inputs */}
        <Grid item xs={12} lg={8}>
          {/* Project linkage */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Project
              </Typography>
              <Autocomplete
                size="small"
                options={projectSearchData?.projects ?? (selectedProject ? [selectedProject] : [])}
                getOptionLabel={(p) => `${p.project_number} — ${p.title}`}
                isOptionEqualToValue={(a, b) => a.id === b.id}
                value={selectedProject}
                onChange={(_, val) => setSelectedProject(val)}
                inputValue={projectSearch}
                onInputChange={(_, val) => setProjectSearch(val)}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search for a project"
                    required
                    helperText={
                      quoteRequest
                        ? `Quote request from ${quoteRequest.full_name} · ${quoteRequest.postcode}`
                        : 'Type to search by project number or title'
                    }
                  />
                )}
                noOptionsText={projectSearch.length < 1 ? 'Start typing to search…' : 'No projects found'}
                sx={{ maxWidth: 480 }}
              />
            </CardContent>
          </Card>

          {/* Property + EPC */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Property Details
              </Typography>

              {/* EPC lookup */}
              <Stack direction="row" spacing={1} mb={2} alignItems="flex-start">
                <TextField
                  size="small"
                  label="Postcode"
                  value={postcode}
                  onChange={(e) => setPostcode(e.target.value.toUpperCase())}
                  inputProps={{ maxLength: 10 }}
                  sx={{ width: 160 }}
                />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={epcLoading ? <CircularProgress size={14} /> : <SearchIcon />}
                  onClick={handleEpcLookup}
                  disabled={epcLoading || !postcode.trim()}
                  sx={{ mt: 0.25 }}
                >
                  EPC Lookup
                </Button>
              </Stack>

              {epcError && (
                <Alert
                  severity={epcError.includes('not configured') ? 'info' : 'warning'}
                  sx={{ mb: 2 }}
                  onClose={() => setEpcError('')}
                >
                  {epcError}
                </Alert>
              )}

              {epcResult && !epcError && (
                <Alert severity={epcResult.found ? 'success' : 'info'} sx={{ mb: 2 }}>
                  {epcResult.found
                    ? `EPC found — ${epcResult.floor_area_m2 ?? '?'} m², age band ${epcResult.construction_age_band ?? '?'}, EPC rating ${epcResult.epc_rating ?? '?'}`
                    : 'No EPC record found for this postcode — enter property details manually below.'}
                </Alert>
              )}

              {/* Customer address from quote request */}
              {quoteRequest && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Customer address:</strong>{' '}
                    {[quoteRequest.address_line_1, quoteRequest.city, quoteRequest.postcode]
                      .filter(Boolean)
                      .join(', ')}
                  </Typography>
                  {quoteRequest.message && (
                    <Typography variant="body2" mt={0.5}>
                      <strong>Message:</strong> {quoteRequest.message}
                    </Typography>
                  )}
                </Alert>
              )}

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    label="Floor area (m²)"
                    type="number"
                    value={floorArea}
                    onChange={(e) => setFloorArea(e.target.value)}
                    fullWidth
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    size="small"
                    label="Construction age band"
                    value={ageBand}
                    onChange={(e) => setAgeBand(e.target.value)}
                    fullWidth
                  >
                    <MenuItem value="">Unknown</MenuItem>
                    {AGE_BAND_OPTIONS.map((o) => (
                      <MenuItem key={o.value} value={o.value}>
                        {o.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    size="small"
                    label="Glazing type"
                    value={glazingType}
                    onChange={(e) => setGlazingType(e.target.value)}
                    fullWidth
                    helperText={epcResult?.windows_description || undefined}
                  >
                    <MenuItem value="">Unknown</MenuItem>
                    <MenuItem value="single">Single glazed</MenuItem>
                    <MenuItem value="double">Double glazed</MenuItem>
                    <MenuItem value="triple">Triple glazed</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    size="small"
                    label="Wall insulation"
                    value={wallInsulation}
                    onChange={(e) => setWallInsulation(e.target.value)}
                    fullWidth
                    helperText={epcResult?.walls_description || undefined}
                  >
                    <MenuItem value="">Unknown</MenuItem>
                    <MenuItem value="cavity_insulated">Cavity insulated</MenuItem>
                    <MenuItem value="cavity_uninsulated">Cavity uninsulated</MenuItem>
                    <MenuItem value="solid">Solid wall</MenuItem>
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    select
                    size="small"
                    label="Loft insulation"
                    value={loftInsulation}
                    onChange={(e) => setLoftInsulation(e.target.value)}
                    fullWidth
                    helperText={epcResult?.roof_description || undefined}
                  >
                    <MenuItem value="">Unknown</MenuItem>
                    <MenuItem value="100mm+">100mm+</MenuItem>
                    <MenuItem value="&lt;100mm">Less than 100mm</MenuItem>
                    <MenuItem value="none">None</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Line items */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6" fontWeight={600}>
                  Line Items
                </Typography>
                <Stack direction="row" spacing={1}>
                  <Button size="small" variant="outlined" onClick={addLabourLine}>
                    + Labour
                  </Button>
                  <Button size="small" variant="outlined" onClick={addBlankLine}>
                    + Blank
                  </Button>
                </Stack>
              </Stack>

              {/* Inventory picker */}
              <Box sx={{ bgcolor: 'grey.50', borderRadius: 1, p: 2, mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Add from inventory
                </Typography>
                <Stack direction="row" spacing={1} mb={1.5}>
                  <TextField
                    size="small"
                    placeholder="Search inventory…"
                    value={inventorySearch}
                    onChange={(e) => setInventorySearch(e.target.value)}
                    InputProps={{
                      startAdornment: <SearchIcon fontSize="small" sx={{ mr: 0.5, color: 'text.secondary' }} />,
                    }}
                    sx={{ flex: 1 }}
                  />
                  <TextField
                    select
                    size="small"
                    value={categoryFilter}
                    onChange={(e) => setCategoryFilter(e.target.value as InventoryCategory | '')}
                    sx={{ minWidth: 150 }}
                    label="Category"
                  >
                    <MenuItem value="">All</MenuItem>
                    {Object.values(InventoryCategory).map((c) => (
                      <MenuItem key={c} value={c}>
                        {INVENTORY_CATEGORY_LABELS[c]}
                      </MenuItem>
                    ))}
                  </TextField>
                </Stack>
                <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                  {inventoryItems.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">
                      No items found
                    </Typography>
                  ) : (
                    inventoryItems.map((item) => (
                      <Stack
                        key={item.id}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{
                          py: 0.75,
                          borderBottom: '1px solid',
                          borderColor: 'divider',
                          '&:last-child': { border: 0 },
                        }}
                      >
                        <Box>
                          <Typography variant="body2" fontWeight={500}>
                            {item.manufacturer} {item.name}
                            {item.capacity_kw ? ` — ${item.capacity_kw} kW` : ''}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {INVENTORY_CATEGORY_LABELS[item.category]} · £{item.unit_price}
                            {item.cost_price ? ` (cost £${item.cost_price})` : ''}
                          </Typography>
                        </Box>
                        <Button
                          size="small"
                          startIcon={<AddIcon />}
                          onClick={() => addInventoryItem(item)}
                        >
                          Add
                        </Button>
                      </Stack>
                    ))
                  )}
                </Box>
              </Box>

              {/* Line items table */}
              {lineItems.length === 0 ? (
                <Typography variant="body2" color="text.secondary" textAlign="center" py={3}>
                  No line items yet — add from inventory or use the buttons above
                </Typography>
              ) : (
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow sx={{ bgcolor: 'grey.50' }}>
                        <TableCell sx={{ minWidth: 200 }}>Description</TableCell>
                        <TableCell align="right" sx={{ width: 70 }}>Qty</TableCell>
                        <TableCell align="right" sx={{ width: 110 }}>Unit Price £</TableCell>
                        <TableCell align="right" sx={{ width: 100 }}>Cost £</TableCell>
                        <TableCell align="right" sx={{ width: 80 }}>VAT %</TableCell>
                        <TableCell align="right" sx={{ width: 110 }}>Total £</TableCell>
                        <TableCell sx={{ width: 40 }} />
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {lineItems.map((li) => {
                        const total = li.quantity * li.unit_price;
                        const margin =
                          li.cost_price != null && li.unit_price > 0
                            ? ((li.unit_price - li.cost_price) / li.unit_price) * 100
                            : null;
                        return (
                          <TableRow key={li._key}>
                            <TableCell>
                              <TextField
                                size="small"
                                value={li.description}
                                onChange={(e) => updateLineItem(li._key, 'description', e.target.value)}
                                fullWidth
                                variant="standard"
                                InputProps={{ disableUnderline: true }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={li.quantity}
                                onChange={(e) =>
                                  updateLineItem(li._key, 'quantity', parseFloat(e.target.value) || 0)
                                }
                                variant="standard"
                                InputProps={{ disableUnderline: true, inputProps: { min: 0, step: 0.5 } }}
                                sx={{ width: 55, input: { textAlign: 'right' } }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={li.unit_price}
                                onChange={(e) =>
                                  updateLineItem(li._key, 'unit_price', parseFloat(e.target.value) || 0)
                                }
                                variant="standard"
                                InputProps={{ disableUnderline: true, inputProps: { min: 0, step: 1 } }}
                                sx={{ width: 90, input: { textAlign: 'right' } }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Tooltip
                                title={
                                  margin !== null
                                    ? `Margin: ${margin.toFixed(1)}%`
                                    : 'No cost set — excluded from margin calc'
                                }
                              >
                                <TextField
                                  size="small"
                                  type="number"
                                  value={li.cost_price ?? ''}
                                  placeholder="—"
                                  onChange={(e) =>
                                    updateLineItem(
                                      li._key,
                                      'cost_price',
                                      e.target.value ? parseFloat(e.target.value) : undefined
                                    )
                                  }
                                  variant="standard"
                                  InputProps={{
                                    disableUnderline: true,
                                    inputProps: { min: 0, step: 1 },
                                  }}
                                  sx={{
                                    width: 80,
                                    input: { textAlign: 'right', color: 'text.secondary' },
                                  }}
                                />
                              </Tooltip>
                            </TableCell>
                            <TableCell align="right">
                              <TextField
                                size="small"
                                type="number"
                                value={li.vat_rate}
                                onChange={(e) =>
                                  updateLineItem(li._key, 'vat_rate', parseFloat(e.target.value) || 0)
                                }
                                variant="standard"
                                InputProps={{ disableUnderline: true, inputProps: { min: 0, max: 100, step: 1 } }}
                                sx={{ width: 55, input: { textAlign: 'right' } }}
                              />
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600}>
                                {fmtCurrency(total)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <IconButton size="small" onClick={() => removeLineItem(li._key)}>
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>

          {/* Financial settings */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={600} mb={2}>
                Financial Settings
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    label="Labour cost £"
                    type="number"
                    value={laborCost}
                    onChange={(e) => setLaborCost(e.target.value)}
                    fullWidth
                    helperText="Used if no labour line item added"
                    inputProps={{ min: 0, step: 100 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    label="BUS Grant £"
                    type="number"
                    value={busGrant}
                    onChange={(e) => setBusGrant(e.target.value)}
                    fullWidth
                    helperText="Default £7,500"
                    inputProps={{ min: 0, step: 500 }}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    size="small"
                    label="Quote valid (days)"
                    type="number"
                    value={validDays}
                    onChange={(e) => setValidDays(e.target.value)}
                    fullWidth
                    inputProps={{ min: 1, max: 365 }}
                  />
                </Grid>
              </Grid>
              <TextField
                size="small"
                label="Notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                multiline
                rows={2}
                fullWidth
                sx={{ mt: 2 }}
              />
            </CardContent>
          </Card>

          <Button
            variant="contained"
            size="large"
            startIcon={
              createMutation.isLoading ? <CircularProgress size={20} color="inherit" /> : <CalcIcon />
            }
            onClick={handleGenerate}
            disabled={createMutation.isLoading}
            fullWidth
          >
            {createMutation.isLoading ? 'Generating…' : 'Generate & Save Quote'}
          </Button>
        </Grid>

        {/* Right — profitability panel (sticky) */}
        <Grid item xs={12} lg={4}>
          <Box sx={{ position: { lg: 'sticky' }, top: { lg: 24 } }}>
            <ProfitPanel
              lineItems={lineItems}
              busGrant={busGrantNum}
              targetMargin={targetMargin}
              onTargetMarginChange={setTargetMargin}
              onApplyMarkup={applyMarkup}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default QuoteCalculatorPage;
